import { MailCheck } from 'lucide-react';
import { useSearch } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

/**
 * /auth/link-sent?email=… — «письмо отправлено» после заказа входа по ссылке.
 *
 * Маршрута не существовало, хотя `pages/auth/login.tsx` уводит сюда каждого,
 * кто нажал «Войти по ссылке». Прод 21.08.2026: человек заказал ссылку, получил
 * 404 → AppShell → `/auth/me` 401 → снова `/login`, пять раз подряд по логу
 * nginx. Подтверждения, что письмо ушло, он не увидел ни разу — письмо нашёл
 * сам, потому что догадался проверить почту.
 *
 * Standalone (без AppShell): сюда приходят неавторизованными по определению.
 */

/**
 * Показывать чужой адрес, пришедший из URL, нельзя без проверки: параметр
 * подставляется кем угодно, а страница отдаётся без авторизации. Пропускаем
 * только то, что выглядит как адрес, — остальное заменяем нейтральным текстом,
 * чтобы страница не превратилась в трибуну для произвольного содержимого.
 */
export function safeEmail(raw: string | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (trimmed.length > 254) return null;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) ? trimmed : null;
}

export function LinkSentPage() {
  const search = useSearch({ from: '/auth/link-sent' }) as { email?: string };
  const email = safeEmail(search.email);

  return (
    <div className="min-h-dvh flex items-center justify-center bg-secondary/40 p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          <MailCheck className="h-8 w-8 mx-auto text-primary" aria-hidden />
          <CardTitle className="text-xl font-semibold">
            Письмо отправлено
          </CardTitle>
          <CardDescription>
            {email ? (
              <>
                Ссылка для входа ушла на <strong>{email}</strong>. Откройте её с
                этого же устройства — ссылка действует 15 минут и срабатывает
                один раз.
              </>
            ) : (
              <>
                Ссылка для входа ушла на указанную почту. Откройте её с этого же
                устройства — ссылка действует 15 минут и срабатывает один раз.
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground text-center">
            Письма нет через пару минут — проверьте папку «Спам».
          </p>
          <Button
            variant="outline"
            className="w-full"
            size="lg"
            onClick={() => window.location.replace('/login')}
          >
            Вернуться ко входу
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default LinkSentPage;
